import * as React from "react";
import { DisablePortalToBodyContext } from "@sproutsocial/seeds-react-portal";

/**
 * Returns the portal container to use for Base UI's Select.Portal /
 * Combobox.Portal. When inside a Modal V2 or Drawer (which set
 * DisablePortalToBodyContext=true), returns a ref'd div rendered inline so
 * the popup stays inside the Radix Dialog's focus/pointer boundary.
 * Otherwise returns undefined, letting Base UI portal to document.body.
 *
 * Module Federation note: when a remote chunk loads its own copy of
 * seeds-react-portal, the context object identity differs from the host's
 * copy, so DisablePortalToBodyContext will always read false in the remote
 * even when a Modal/Drawer/PeekIn has provided true. As a fallback we walk
 * the DOM from the containerRef anchor and treat any [role="dialog"] ancestor
 * as an in-dialog boundary, matching the same behaviour the context would have
 * triggered.
 *
 * Usage:
 *   const { containerRef, portalContainer } = useSeedsPortalContainer();
 *   <div ref={containerRef} style={{ position: "relative" }} />
 *   <Select.Portal container={portalContainer}>...</Select.Portal>
 */
export function useSeedsPortalContainer() {
  const disablePortalToBody = React.useContext(DisablePortalToBodyContext);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = React.useState<
    HTMLElement | undefined
  >(undefined);

  React.useEffect(() => {
    if (!containerRef.current) return;

    // Primary signal: context set by Modal V2 / Drawer / PeekIn in the same
    // module federation chunk.
    const insideDialog =
      disablePortalToBody ||
      // Fallback for cross-chunk MFE scenarios where context identity differs:
      // detect the dialog boundary directly from the DOM.
      !!containerRef.current.closest("[role='dialog']");

    if (insideDialog) {
      // Portal to the Radix Dialog content element so the popup escapes the
      // ModalBody scroll container while staying inside the Dialog boundary
      // (required for Radix focus/pointer-events containment).
      const dialogContent =
        containerRef.current.closest<HTMLElement>("[role='dialog']");
      setPortalContainer(dialogContent ?? containerRef.current);
    }
  }, [disablePortalToBody]);

  return { containerRef, portalContainer, disablePortalToBody };
}
